import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { getSankeyTitle } from 'diagram/title';
import { Box, HStack, VStack } from 'ui/Layout';
import Link, { LinkBox, LinkOverlay } from 'ui/Link';
import Skeleton from 'ui/Skeleton';
import Tooltip from 'ui/Tooltip';
import { Text } from 'ui/Typography';
import { sankeyToForm } from 'utils/form';

import { Sankey } from '../../generated-api';
import { pathToSankey } from '../../utils/routes';
import { canUserEditDiagram } from '../../utils/team';
import { useCurrentUser } from '../../utils/user';
import CreatedFromExcelDataIcon from '../CreatedFromExcelDataIcon';
import ViewOnlyInfo from '../ViewOnlyInfo';
import DeleteDiagramButton from './DeleteDiagramButton';
import DiagramCreatedInfo from './DiagramCreatedInfo';

type Props = {
  currentSankeyId?: number;
  onSelect?: () => void;
  title: string;
  headerContent?: React.ReactNode;
  emptyContent: React.ReactNode;
  sankeys: Sankey[];
  isLoading: boolean;
  refetchDiagrams: () => Promise<unknown>;
  canDelete?: boolean;
};

const DiagramsListing: React.FC<Props> = ({
  currentSankeyId,
  onSelect,
  title,
  headerContent,
  emptyContent,
  sankeys,
  isLoading,
  refetchDiagrams,
  canDelete = false,
}) => {
  const { user } = useCurrentUser();
  const navigate = useNavigate();

  const handleSelect = useCallback(
    (sankeyId: number) => {
      onSelect?.();
      navigate(pathToSankey(sankeyId));
    },
    [onSelect]
  );

  return (
    // Use full width on mobile and at least 500px on desktop (where it can appear in multiple columns)
    <Box flex={1} minW={{ base: '100%', md: '500px' }}>
      <HStack mb={3}>
        <Text fontSize="xl">{title}</Text>
        {headerContent}
      </HStack>
      {isLoading ? (
        <VStack alignItems="stretch">
          <Skeleton.Row height="40px" />
          <Skeleton.Row height="40px" />
          <Skeleton.Row height="40px" />
          <Skeleton.Row height="40px" />
        </VStack>
      ) : sankeys.length === 0 ? (
        <>{emptyContent}</>
      ) : (
        <VStack alignItems="stretch" gap={0}>
          {sankeys.map((sankey) => (
            <HStack key={sankey.id} _hover={{ background: 'gray.100' }}>
              <Tooltip content="Click to open this diagram">
                <LinkBox
                  cursor="pointer"
                  flex={1}
                  p={1.5}
                  minW={0}
                  _hover={{ pl: 2 }}
                >
                  <LinkOverlay asChild>
                    <Link
                      display="block"
                      fontSize="md"
                      onClick={() => handleSelect(sankey.id)}
                      truncate
                    >
                      {getSankeyTitle(sankeyToForm(sankey))}
                      {sankey.data_origin === 'excel' && (
                        <CreatedFromExcelDataIcon
                          display="inline-block"
                          ml={1}
                          mb={-1}
                        />
                      )}
                    </Link>
                  </LinkOverlay>
                  <HStack>
                    <DiagramCreatedInfo sankey={sankey} />
                    {!canUserEditDiagram(sankey, user) && <ViewOnlyInfo />}
                  </HStack>
                </LinkBox>
              </Tooltip>
              {canDelete && (
                <DeleteDiagramButton
                  sankey={sankey}
                  currentSankeyId={currentSankeyId}
                  refetchDiagrams={refetchDiagrams}
                />
              )}
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default DiagramsListing;
